import { useAuth } from "@clerk/clerk-react";
import { Box, CircularProgress, Fab } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { unstable_usePrompt } from "react-router-dom";
import { BentoCategory, OrganizationProfile } from "schemas/dashboard";
import { Map } from "schemas/functions";

import { PAGE_VISITED } from "constants/trackingProps";
import AboutForm from "features/Auth/Onboarding/AboutForm";
import IncomeForm from "features/Auth/Onboarding/IncomeForm";
import Introduction from "features/Auth/Onboarding/Introduction";
import EngagementForm from "features/Influencer/Profile/EngagementForm";
import PartnershipForm from "features/Influencer/Profile/PartnershipForm";
import PortfolioForm from "features/Influencer/Profile/PortfolioForm";
import RecommendationForm from "features/Influencer/Profile/RecommendationForm";
import SignatureForm from "features/Influencer/Profile/SignatureForm";
import StandOutForm from "features/Influencer/Profile/StandOutForm";
import { fetcherAuth } from "utils/api";
import { removeDuplicates } from "utils/object";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import DetailedCategoryForm from "./DetailedCategoryForm";
import styles from "./styles";

const Profile = () => {
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const {
    currentUser,
    setCurrentUser,
    currentOrg,
    setCurrentOrg,
    profile,
    setProfile,
    includedBentoSearchFilterIds,
    excludedBentoSearchFilterIds,
  } = useContext(OrganizationUserContext);
  const [loading, setLoading] = useState(false);
  const { tabWidth, discoverTab } = useContext(DiscoverViewContext);
  const { topLevelCategories, fetchBentoCategories } = useContext(
    BentoCategoriesContext,
  );

  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [isDirty, setIsDirty] = useState(false);
  const [needsUpdateCategory, setNeedsUpdateCategory] = useState(false);

  const categories = currentOrg?.bentoCategories;

  const [excludeIncludeFilterHasChanged, setExcludeIncludeFilterHasChanged] =
    useState(false);

  const saveOnboardingForm = async () => {
    const unfilledRequireFields = validateRequiredFields();
    if (unfilledRequireFields?.length > 0) {
      setAlert(
        `Please fill out required fields in ${unfilledRequireFields?.join(
          ", ",
        )}`,
        "warning",
      );
      return;
    }
    if (!categories || categories?.length === 0) {
      setAlert("Please select at least one category", "warning");
      return;
    }

    if (excludeIncludeFilterHasChanged) {
      handleSaveFilters();
    }

    try {
      if (needsUpdateCategory) {
        const cleanCategories = removeDuplicates(categories, "id");
        updateCategory(cleanCategories);
      }
      setLoading(true);
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        { ...onboardingForm, page: "Profile" },
      );
      setProfile((prev) => ({
        ...prev,
        ...organizationProfile,
      }));
      setCurrentUser((prev) => {
        if (prev) {
          prev.name = onboardingForm.name;
        }
        return prev;
      });
      setAlert("Successfully updated profile", "success");
      setIsDirty(false);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFilters = async () => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/search-filters`,
        "PUT",
        {},
        {
          include: includedBentoSearchFilterIds,
          exclude: excludedBentoSearchFilterIds,
        },
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  unstable_usePrompt({
    message:
      "You have unsaved changes. Are you sure you want to navigate away from this page?",
    when: ({ currentLocation, nextLocation }) =>
      Boolean(isDirty) && currentLocation.pathname !== nextLocation.pathname,
  });

  const hasDifference = () => {
    if (!profile || Object.keys(onboardingForm)?.length === 0) return;
    for (const key in profile) {
      if (
        key in onboardingForm &&
        JSON.stringify(profile[key as keyof OrganizationProfile]) !==
          JSON.stringify(onboardingForm[key])
      ) {
        return true;
      }
    }
    return false;
  };

  const updateCategory = async (categories: BentoCategory[]) => {
    try {
      const { categories: updatedCategories } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/categories`,
        "PUT",
        {},
        {
          categoryIds: categories?.map((x) => x.id),
        },
      );
      setCurrentOrg((prev) => {
        const copy = makeDeepCopy(prev);
        copy.bentoCategories = updatedCategories;
        return copy;
      });
      setNeedsUpdateCategory(false);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const validateRequiredFields = () => {
    const requiredKeys: Map = {
      name: "Name",
      addressComponents: "Location",
      preferredBrandSize: "Preferred Brand Size",
      introduction: "About Me",
    };
    const missingKeys = [];
    for (const key in requiredKeys) {
      const value = onboardingForm[key];
      if (
        value?.length === 0 ||
        // @ts-ignore: Guaranteed type is string
        (typeof value === "string" && value?.trim()?.length === 0)
      ) {
        missingKeys.push(requiredKeys[key]);
      }
    }
    return missingKeys;
  };

  useEffect(() => {
    if (!isDirty && hasDifference()) {
      setIsDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingForm]);

  useEffect(() => {
    if (profile?.id) {
      setOnboardingForm({
        ...profile,
        name: currentUser?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (topLevelCategories.length === 0) {
      fetchBentoCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <Box sx={styles.page}>
      <AboutForm
        onboardingForm={onboardingForm}
        setOnboardingForm={setOnboardingForm}
        handleNext={saveOnboardingForm}
        hideDiscoveryQuestion
        isProfilePage={true}
      />
      {onboardingForm?.id && (
        <IncomeForm
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
          handleNext={saveOnboardingForm}
          isProfilePage={true}
        />
      )}
      <DetailedCategoryForm
        setNeedsUpdateCategory={setNeedsUpdateCategory}
        topLevelCategories={topLevelCategories}
      />
      <Introduction
        onboardingForm={onboardingForm}
        setOnboardingForm={setOnboardingForm}
        saveOnboardingForm={saveOnboardingForm}
      />
      <PortfolioForm
        onboardingForm={onboardingForm}
        setOnboardingForm={setOnboardingForm}
        isProfilePage={true}
      />
      <PartnershipForm
        onboardingForm={onboardingForm}
        setOnboardingForm={setOnboardingForm}
        isProfilePage={true}
      />
      <EngagementForm
        onboardingForm={onboardingForm}
        setOnboardingForm={setOnboardingForm}
        isProfilePage={true}
      />
      <StandOutForm
        onboardingForm={onboardingForm}
        setOnboardingForm={setOnboardingForm}
        isProfilePage={true}
      />
      <SignatureForm
        onboardingForm={onboardingForm}
        setOnboardingForm={setOnboardingForm}
        saveOnboardingForm={saveOnboardingForm}
      />
      <RecommendationForm
        onboardingForm={onboardingForm}
        setOnboardingForm={setOnboardingForm}
        saveOnboardingForm={saveOnboardingForm}
        isProfilePage={true}
        setExcludeIncludeFilterHasChanged={setExcludeIncludeFilterHasChanged}
      />

      <Box sx={styles.button(tabWidth, discoverTab !== null)}>
        <Fab
          disabled={loading}
          variant="extended"
          color="primary"
          onClick={saveOnboardingForm}
        >
          <Box component="i" className="fa-regular fa-save" sx={{ mr: 1 }} />
          {loading ? <CircularProgress size={20} /> : "Save"}
        </Fab>
      </Box>
    </Box>
  );
};

export default Profile;
